import type {
  DashboardData,
  PikbDashboardData,
  StoredPikbSubmission,
  StoredSubmission,
  SurveyEvent,
} from "../types";
import {
  getOptionLabel,
  normalizeAgeGroupValue,
} from "../data/survey";
import { normalizeMukimValue } from "../data/locations";

export type BreakdownItem = {
  name: string;
  value: number;
};

export type DailyItem = {
  date: string;
  submissions: number;
  starts: number;
};

export function percent(value: number, total: number) {
  if (!total) return 0;
  return Math.round((value / total) * 100);
}

export function uniqueStartedSessions(events: SurveyEvent[]) {
  return new Set(
    events
      .filter((event) => event.event_type === "started")
      .map((event) => event.session_key),
  ).size;
}

type TimeSeriesData = {
  submissions: Array<{ created_at: string }>;
  events: SurveyEvent[];
};

export function completionRate(data: DashboardData | PikbDashboardData) {
  const starts = uniqueStartedSessions(data.events);
  return percent(data.submissions.length, starts || data.submissions.length);
}

export function countBy<K extends keyof StoredSubmission>(
  submissions: StoredSubmission[],
  key: K,
  labelLanguage: "ms" | "en" = "ms",
) {
  const map = new Map<string, number>();
  submissions.forEach((submission) => {
    const rawValue = String(submission[key] ?? "");
    if (!rawValue) return;
    const value =
      key === "age_group"
        ? normalizeAgeGroupValue(rawValue)
        : key === "mukim"
          ? normalizeMukimValue(rawValue)
          : rawValue;
    map.set(value, (map.get(value) ?? 0) + 1);
  });

  return Array.from(map.entries())
    .map(([name, value]) => ({
      name:
        key === "gender" ||
        key === "age_group" ||
        key === "employment_status" ||
        key === "mukim" ||
        key === "nama_kampung"
          ? getOptionLabel(key, name, labelLanguage)
          : name,
      value,
    }))
    .sort((a, b) => b.value - a.value);
}

export function dailySeries(data: TimeSeriesData): DailyItem[] {
  const map = new Map<string, DailyItem>();
  const ensure = (iso?: string) => {
    const date = iso ? iso.slice(0, 10) : new Date().toISOString().slice(0, 10);
    const existing = map.get(date) ?? { date, submissions: 0, starts: 0 };
    map.set(date, existing);
    return existing;
  };

  data.submissions.forEach((submission) => {
    ensure(submission.created_at).submissions += 1;
  });

  data.events
    .filter((event) => event.event_type === "started")
    .forEach((event) => {
      ensure(event.created_at).starts += 1;
    });

  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

export function exportCsv(submissions: StoredSubmission[]) {
  const headers: Array<keyof StoredSubmission> = [
    "id",
    "created_at",
    "language",
    "gender",
    "age_group",
    "employment_status",
    "nama_kampung",
    "mukim",
    "main_issue",
    "development_suggestion",
  ];

  const escape = (value: unknown) => {
    const text = String(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = [
    headers.join(","),
    ...submissions.map((submission) =>
      headers.map((header) => escape(submission[header])).join(","),
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `putatan-development-survey-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function exportPikbCsv(submissions: StoredPikbSubmission[]) {
  const headers = [
    "id",
    "created_at",
    "language",
    "zone",
    "category",
    "leader_name",
    "leader_ic",
    "leader_phone",
    "leader_address",
    "members",
    "innovation_title",
    "innovation_summary",
  ] as const;

  const escape = (value: unknown) => {
    const text =
      typeof value === "string" ? value : JSON.stringify(value ?? "");
    return `"${text.replace(/"/g, '""')}"`;
  };

  const csv = [
    headers.join(","),
    ...submissions.map((submission) =>
      headers
        .map((header) =>
          header === "members"
            ? escape(
                submission.members
                  .map(
                    (member, index) =>
                      `${index + 1}. ${member.name} | ${member.ic_number} | ${member.phone}`,
                  )
                  .join("; "),
              )
            : escape(submission[header]),
        )
        .join(","),
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `pikb-2026-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}
